import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, ArrowRight, MessageCircle, Check, Video, GraduationCap, Gamepad2, TrendingUp, ChevronDown, Sparkles, Users, Orbit, LifeBuoy, Clock, Cpu, Scale } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ChessBoard from "@/components/ChessBoard";
import LevelBadge from "@/components/LevelBadge";
import Logo from "@/components/Logo";
import api, { track } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: Video, title: "ASSISTA", text: "Aprenda o conceito em uma videoaula clara e objetiva." },
  { icon: GraduationCap, title: "ENTENDA", text: "Veja exemplos e explicações passo a passo." },
  { icon: Gamepad2, title: "JOGUE", text: "Resolva a posição diretamente no tabuleiro interativo." },
  { icon: TrendingUp, title: "EVOLUA", text: "Ganhe XP, avance de nível e acompanhe seu desenvolvimento." },
];

const METHODS = [
  { icon: Sparkles, img: "/courses/m-crazy.jpg", title: "Xadrez Crazy", text: "Capturou uma peça? Coloque-a de volta no tabuleiro ao seu favor! O modo Crazyhouse cria ataques insanos e reviravoltas eletrizantes." },
  { icon: Users, img: "/courses/m-4p.jpg", title: "Xadrez de 4", text: "Quatro jogadores, um só tabuleiro. Alianças, blefes e caos tático em tempo real — o xadrez como você nunca viu." },
  { icon: Orbit, img: "/courses/m-green.jpg", title: "Chess Ball", text: "Xadrez encontra futebol: use a lógica das peças para levar a bola ao gol. Raciocínio e diversão no mesmo jogo." },
  { icon: Cpu, title: "Domine o ChessBase", text: "Nunca usou o ChessBase? Aqui você aprende do zero a analisar como os profissionais — e não fica sozinho: tem suporte e acompanhamento de verdade." },
  { icon: Scale, title: "Arbitragem de xadrez", text: "Não entende nada de arbitragem? Agora vai entender: regras oficiais, relógios e como conduzir uma partida como um árbitro." },
];

const FEATURES = [
  { icon: Gamepad2, title: "+1.500 games de xadrez", text: "Uma biblioteca gigante de partidas e desafios para você praticar sem parar." },
  { icon: LifeBuoy, title: "Suporte 24h", text: "Não gostou de algo? Me chama que a gente resolve — atendimento humano a qualquer hora." },
  { icon: Sparkles, title: "Aprendizado lúdico", text: "Aprenda de forma divertida, com e sem tecnologia, do jeito que funciona para você." },
  { icon: Clock, title: "No seu ritmo, sem pressão", text: "Sem prazos e sem cobrança. Você avança quando e como quiser." },
  { icon: Video, title: "Chamadas interativas online", text: "Aulas ao vivo e interativas, entendendo cada aluno e a sua necessidade." },
  { icon: Users, title: "Ensino personalizado", text: "Desenvolvemos uma pessoa única e habilidosa — o xadrez a serviço de você." },
];

const CHESS_QUALITIES = ["Concentração", "Raciocínio lógico", "Paciência", "Tomada de decisão", "Planejamento", "Criatividade", "Disciplina", "Memória", "Autocontrole", "Visão estratégica"];

const LEVELS = [
  { key: "pawn", state: "concluido" }, { key: "knight", state: "atual" },
  { key: "bishop", state: "disponivel" }, { key: "rook", state: "disponivel" },
  { key: "queen", state: "bloqueado" }, { key: "king", state: "bloqueado" },
];

const BENEFITS = [
  "Todos os cursos", "Videoaulas", "Tabuleiro interativo", "Puzzles",
  "Exercícios", "Sistema de níveis", "Progresso salvo", "Novas aulas", "Acesso pelo celular",
];

const FAQ = [
  { q: "Preciso saber jogar xadrez?", a: "Não. A jornada começa desde o nível iniciante, do absoluto zero." },
  { q: "As aulas são apenas em vídeo?", a: "Não. A ChessUp Academy combina vídeo, explicação, exercícios, puzzles e tabuleiro interativo." },
  { q: "Posso estudar pelo celular?", a: "Sim. A plataforma é totalmente responsiva e funciona lindamente no celular." },
  { q: "Meu progresso fica salvo?", a: "Sim. Seu progresso permanece vinculado à sua conta, sempre." },
  { q: "Posso cancelar minha assinatura?", a: "Sim. O cancelamento impede a próxima renovação e o acesso permanece durante o período já pago." },
];

export default function Home() {
  const { settings, openWhatsapp } = useSettings();
  const { user } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const goStart = () => { track("signup_started", "homepage"); nav(user ? "/painel" : "/criar-conta"); };

  return (
    <div className="bg-chessup-bg min-h-screen">
      <Navbar />
      <FloatingWhatsApp source="homepage" />

      {/* HERO */}
      <section id="inicio" className="relative pt-28 pb-20 lg:pt-36 overflow-hidden grain">
        <div className="absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,212,0.18), transparent 70%)" }} />
        <div className="absolute top-1/3 -left-40 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.10), transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center relative">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-chessup-gold/40 bg-chessup-gold/10 px-3 py-1 text-xs font-semibold text-chessup-goldlight tracking-wide mb-6">
              ♞ Academia de xadrez premium
            </div>
            <div className="font-heading text-chessup-turq text-sm tracking-[0.3em] mb-3">CHESSUP ACADEMY</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-chessup-text leading-[1.05]">
              Aprenda. <span className="chessup-gold-text">Jogue.</span> Evolua.
            </h1>
            <p className="mt-5 text-xl sm:text-2xl text-chessup-text font-display font-semibold leading-snug">
              Você não assiste xadrez. <span className="chessup-gold-text">Você joga a aula.</span>
            </p>
            <p className="mt-3 text-base text-chessup-subtext max-w-lg leading-relaxed">
              Videoaulas, exercícios interativos, puzzles e uma jornada completa para transformar seu xadrez.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={goStart} data-testid="hero-cta-start" className="inline-flex items-center justify-center gap-2 rounded-full bg-chessup-gold px-7 py-3.5 font-semibold text-chessup-deep hover:bg-chessup-goldlight transition-colors shadow-[0_0_28px_rgba(0,229,212,0.3)]">
                COMEÇAR AGORA <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => openWhatsapp("homepage")} data-testid="hero-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-full border border-chessup-border px-7 py-3.5 font-semibold text-chessup-text hover:border-chessup-turq hover:shadow-[0_0_24px_rgba(0,229,212,0.25)] transition-all">
                <MessageCircle className="h-4 w-4" /> FALAR NO WHATSAPP
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 font-mono text-xs tracking-widest text-chessup-subtext">
              <span className="inline-flex items-center gap-2"><Video className="h-4 w-4 text-chessup-turq" /> VIDEOAULAS</span>
              <span className="inline-flex items-center gap-2"><Gamepad2 className="h-4 w-4 text-chessup-turq" /> TABULEIRO INTERATIVO</span>
              <span className="inline-flex items-center gap-2"><TrendingUp className="h-4 w-4 text-chessup-turq" /> PROGRESSO E NÍVEIS</span>
            </div>
          </div>

          {/* Leão + tabuleiro com brilho turquesa */}
          <div className="relative animate-fade-up min-h-[400px] sm:min-h-[520px] flex items-center justify-center" style={{ animationDelay: "0.15s" }} data-testid="hero-visual">
            <div className="absolute h-[320px] w-[320px] sm:h-[440px] sm:w-[440px] rounded-full border border-chessup-turq/30 animate-pulse-glow" style={{ background: "radial-gradient(circle, rgba(0,229,212,0.16), rgba(0,229,212,0.03) 60%, transparent 72%)" }} />
            <div className="absolute h-[380px] w-[380px] sm:h-[520px] sm:w-[520px] rounded-full border border-dashed border-chessup-gold/25" />
            <img
              src="/chessup-logo.png"
              alt="Leão da ChessUp Academy"
              className="relative w-[250px] sm:w-[350px] animate-float"
              style={{ filter: "drop-shadow(0 0 34px rgba(212,175,55,0.45)) drop-shadow(0 0 70px rgba(0,229,212,0.22))" }}
            />
            <div
              className="absolute bottom-0 left-0 sm:left-2 w-36 sm:w-48 rounded-2xl border border-chessup-turq/50 bg-chessup-deep p-2 shadow-[0_0_34px_rgba(0,229,212,0.3)]"
              style={{ transform: "perspective(700px) rotateY(16deg) rotateX(6deg)" }}
            >
              <ChessBoard fen="r3k3/8/4N3/8/8/8/8/4K3 w - - 0 1" interactive={false} />
            </div>
            <div className="absolute top-4 right-0 sm:right-2 glass rounded-xl px-4 py-2.5 animate-float-slow">
              <div className="font-heading text-lg text-chessup-goldlight leading-none">+1.500</div>
              <div className="mt-1 text-[11px] font-mono tracking-widest text-chessup-subtext">GAMES DE XADREZ</div>
            </div>
            <div className="absolute bottom-10 right-0 sm:right-2 glass rounded-xl px-4 py-2.5 animate-float-slow" style={{ animationDelay: "1.5s" }}>
              <div className="font-heading text-lg text-chessup-goldlight leading-none">24h</div>
              <div className="mt-1 text-[11px] font-mono tracking-widest text-chessup-subtext">SUPORTE HUMANO</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="py-20 border-t border-chessup-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">Uma nova forma de aprender xadrez</h2>
            <p className="mt-4 font-mono text-chessup-turq text-sm tracking-widest">APRENDA → JOGUE → ACERTE → EVOLUA</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-xl border border-chessup-border bg-chessup-card p-6 hover:border-chessup-turq/50 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="h-10 w-10 rounded-lg bg-chessup-turq/12 flex items-center justify-center text-chessup-turq"><Icon className="h-5 w-5" /></span>
                    <span className="font-mono text-xs text-chessup-subtext">0{i + 1}</span>
                  </div>
                  <h3 className="font-heading text-xl text-chessup-text">{s.title}</h3>
                  <p className="mt-2 text-sm text-chessup-subtext leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section id="metodologia" className="py-20 bg-chessup-deep/50 border-y border-chessup-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">A jornada do Peão ao Rei</h2>
          <p className="mt-4 text-chessup-subtext max-w-2xl mx-auto">Seis níveis de maestria. Cada conquista aproxima você do topo.</p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {LEVELS.map((l, i) => (
              <div key={l.key} className="flex items-center gap-4 sm:gap-6">
                <div className="flex flex-col items-center gap-2">
                  <LevelBadge levelKey={l.key} state={l.state} size={72} />
                  <span className="text-xs text-chessup-subtext capitalize">{["Peão","Cavalo","Bispo","Torre","Dama","Rei"][i]}</span>
                </div>
                {i < LEVELS.length - 1 && <ArrowRight className="h-5 w-5 text-chessup-border" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UNIQUE METHODS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-chessup-gold/40 bg-chessup-gold/10 px-3 py-1 text-xs font-semibold text-chessup-goldlight tracking-wide mb-4">EXCLUSIVO CHESSUP</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">Métodos únicos e diferenciados</h2>
            <p className="mt-4 text-chessup-subtext">Do Chess Ball ao ChessBase: modos e formações exclusivas para você entrar no mundo do xadrez e desvendar a magia das 64 casas e sua infinitude.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="rounded-2xl border border-chessup-border bg-chessup-card p-7 hover:border-chessup-gold/50 transition-all group" data-testid={`method-${m.title}`}>
                  {m.img ? (
                    <div className="mb-5 rounded-xl overflow-hidden aspect-video border border-chessup-border">
                      <img src={m.img} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <span className="h-12 w-12 rounded-xl bg-chessup-gold/12 flex items-center justify-center text-chessup-goldlight mb-5 group-hover:scale-105 transition-transform"><Icon className="h-6 w-6" /></span>
                  )}
                  <h3 className="font-heading text-xl text-chessup-text">{m.title}</h3>
                  <p className="mt-2 text-sm text-chessup-subtext leading-relaxed">{m.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHESSUP */}
      <section className="py-20 bg-chessup-deep/50 border-y border-chessup-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">Por que a ChessUp Academy</h2>
            <p className="mt-4 text-chessup-subtext">Muito mais que aulas: uma experiência completa, humana e feita para você evoluir de verdade.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-xl border border-chessup-border bg-chessup-card p-6 hover:border-chessup-turq/50 transition-all" data-testid={`feature-${f.title}`}>
                  <span className="h-10 w-10 rounded-lg bg-chessup-turq/12 flex items-center justify-center text-chessup-turq mb-4"><Icon className="h-5 w-5" /></span>
                  <h3 className="font-heading text-lg text-chessup-text">{f.title}</h3>
                  <p className="mt-2 text-sm text-chessup-subtext leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 rounded-2xl border border-chessup-gold/30 bg-chessup-card p-8 text-center">
            <h3 className="font-heading text-2xl text-chessup-text">O que o xadrez desenvolve em você</h3>
            <p className="mt-2 text-sm text-chessup-subtext">Habilidades que vão muito além do tabuleiro.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2.5">
              {CHESS_QUALITIES.map((q) => (
                <span key={q} className="rounded-full border border-chessup-gold/40 bg-chessup-gold/10 px-4 py-1.5 text-sm text-chessup-goldlight">{q}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COURSES PREVIEW */}
      <section id="cursos" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text mb-10">Cursos em destaque</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 6).map((c) => (
              <Link key={c.id} to={user ? `/cursos/${c.id}` : "/criar-conta"} data-testid={`home-course-${c.id}`} className="group rounded-xl border border-chessup-border bg-chessup-card overflow-hidden hover:border-chessup-turq/50 transition-all">
                <div className="relative aspect-video overflow-hidden">
                  <img src={c.cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {c.is_new && <span className="absolute top-2 left-2 rounded-full bg-chessup-gold/90 px-2.5 py-0.5 text-xs font-semibold text-chessup-deep">NOVO</span>}
                </div>
                <div className="p-5">
                  <span className="text-xs text-chessup-turq font-mono uppercase">{c.difficulty}</span>
                  <h3 className="font-heading text-lg text-chessup-text mt-1">{c.title}</h3>
                  <p className="mt-2 text-sm text-chessup-subtext line-clamp-2">{c.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="assinatura" className="py-20 bg-chessup-deep/50 border-y border-chessup-border/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">Assine a ChessUp Academy</h2>
            <p className="mt-3 text-chessup-subtext">Um plano simples. Acesso completo.</p>
          </div>
          <div className="rounded-2xl border border-chessup-gold/40 bg-chessup-card p-8 shadow-[0_0_40px_rgba(212,175,55,0.12)]">
            <div className="flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-chessup-gold/15 border border-chessup-gold/40 px-3 py-1 text-xs font-semibold text-chessup-goldlight">PREMIUM</div>
                <h3 className="font-heading text-2xl text-chessup-text mt-3">{settings.plan_name}</h3>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-chessup-text">{formatPrice(settings.price_amount, settings.price_currency)}</div>
                <div className="text-sm text-chessup-subtext">/mês</div>
              </div>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-chessup-text"><Check className="h-4 w-4 text-chessup-turq shrink-0" /> {b}</li>
              ))}
            </ul>
            <button onClick={goStart} data-testid="pricing-subscribe" className="mt-8 w-full rounded-full bg-chessup-gold py-3.5 font-semibold text-chessup-deep hover:bg-chessup-goldlight transition-colors">
              ASSINAR CHESSUP ACADEMY
            </button>
            <button onClick={() => openWhatsapp("pricing")} data-testid="pricing-whatsapp" className="mt-3 w-full rounded-full border border-chessup-border py-3 text-sm font-semibold text-chessup-text hover:border-chessup-turq transition-colors">
              TIRAR DÚVIDA NO WHATSAPP
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text text-center mb-10">Perguntas frequentes</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-chessup-border bg-chessup-card overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-${i}`} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium text-chessup-text">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 text-chessup-turq transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-sm text-chessup-subtext leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => openWhatsapp("faq")} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white hover:opacity-90">
              <MessageCircle className="h-4 w-4" fill="currentColor" /> FALAR COM A CHESSUP NO WHATSAPP
            </button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-chessup-deep">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Logo size={56} className="justify-center mb-6" />
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-chessup-text">Comece sua jornada hoje</h2>
          <p className="mt-3 text-chessup-subtext">Você não assiste xadrez. Você joga a aula.</p>
          <button onClick={goStart} data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-chessup-gold px-8 py-4 font-semibold text-chessup-deep hover:bg-chessup-goldlight transition-colors shadow-[0_0_28px_rgba(0,229,212,0.3)]">
            COMEÇAR AGORA <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
